import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface ITabOption {
  label: string;
  content: ReactNode;
  disabled?: boolean;
  disabledTitle?: string;
}

interface ITabsProps {
  options: ITabOption[];
  initialTabIndex?: number;
  /** Controlled active tab index. When provided, Tabs becomes controlled. */
  activeTabIndex?: number;
  className?: string;
  onTabChange?: (tabIndex: number) => void;
  /** When true, the tab content area is not rendered (useful when tabs are used purely for navigation). */
  hideContent?: boolean;
}

const Tabs: React.FC<ITabsProps> = ({
  options,
  initialTabIndex = 0,
  activeTabIndex,
  className = '',
  onTabChange,
  hideContent = false,
}) => {
  const [activeTab, setActiveTab] = useState(initialTabIndex);

  // Sync internal state when used as a controlled component
  useEffect(() => {
    if (typeof activeTabIndex === 'number' && activeTabIndex !== activeTab) {
      setActiveTab(activeTabIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex]);

  const handleTabChange = (tabIndex: number) => {
    // Don't allow switching to disabled tabs
    if (options[tabIndex]?.disabled) {
      return;
    }

    // If controlled, rely on parent state; otherwise update internal state
    if (typeof activeTabIndex !== 'number') {
      setActiveTab(tabIndex);
    }
    if (onTabChange) {
      onTabChange(tabIndex);
    }
  };

  return (
    <>
      <div className={`w-full ${className}`}>
        {/* Mobile select (Tailwind UI pattern) */}
        <div className='sm:hidden'>
          <label htmlFor='tabs' className='sr-only'>
            Select a tab
          </label>
          <select
            id='tabs'
            name='tabs'
            className='block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-theme-500 focus:outline-none focus:ring-theme-500 sm:text-sm'
            value={activeTab}
            onChange={(e) => handleTabChange(Number(e.target.value))}
          >
            {options.map((tab, idx) => (
              <option key={tab.label} value={idx} disabled={tab.disabled}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop underline tabs (Tailwind UI style, themed) */}
        <div className='hidden sm:block'>
          <div className='border-b border-gray-200'>
            <nav className='-mb-px flex w-full gap-2' aria-label='Tabs' role='tablist'>
              {options.map((tab, idx) => {
                const isActive = activeTab === idx;
                const isDisabled = Boolean(tab.disabled);

                return (
                  <button
                    key={tab.label}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                    title={isDisabled ? tab.disabledTitle : undefined}
                    className={[
                      'flex-1 whitespace-nowrap border-b-2 px-3 py-3 text-center text-md font-medium transition-colors',
                      // Focus (keyboard or mouse): no box outline/ring/shadow; only bottom underline
                      'focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none focus:border-theme-500',
                      'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-none focus-visible:border-theme-500',
                      isActive
                        ? 'border-theme-500 bg-theme-100 text-theme-700 rounded-[10px]'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-theme-700',
                      isDisabled
                        ? 'cursor-not-allowed opacity-50 hover:border-transparent hover:text-gray-500'
                        : '',
                    ].join(' ')}
                    onClick={() => handleTabChange(idx)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {!hideContent && (
        <div className='overflow-y-auto mt-2 min-h-[500px]'>{options[activeTab]?.content}</div>
      )}
    </>
  );
};

export default Tabs;
